// Kyte normalize + validate + split.
//
// Input to normalize(): a per-offer payload where flightSolutions are
// already resolved (not ID references). Use split_offers_from_response to
// flatten a raw shop-flights response (offers + flightSolutions dict) into
// per-offer payloads before normalization.
//
// "negociated" is the authentic Kyte fareType enum value — preserved in
// _raw and normalized to "NEGOTIATED" on output so downstream code stays
// agnostic to the upstream misspelling.

import {
  as_string,
  as_number,
  is_non_empty_array,
  is_non_empty_object,
  minor_to_decimal,
  minutes_to_iso_duration,
  build_empty_endpoint,
  build_meta,
  build_frozen_raw,
  sum_itinerary_durations,
  collect_marketing_carriers,
  with_currency_symbol,
  finalize_itinerary,
  lift_first_fare_amenities,
} from '../base/helpers';
import { ensure_infant_associations } from './helpers';
import { is_bundle_service } from './resources/ancillaries';
import { kyte_amenities_for_fare } from './resources/amenities';

// ─── Enum mappers ────────────────────────────────────────────────────────

const map_pax_type = (kyte_type) => {
  const t = as_string(kyte_type).toLowerCase();
  if (t === 'adult') {
    return 'ADULT';
  }
  if (t === 'child') {
    return 'CHILD';
  }
  if (t === 'infant') {
    return 'HELD_INFANT';
  }
  return as_string(kyte_type).toUpperCase();
};

const map_cabin = (kyte_cabin) => {
  const c = as_string(kyte_cabin).toLowerCase();
  if (c === 'economy') {
    return 'ECONOMY';
  }
  if (
    c === 'premium-economy' ||
    c === 'premium_economy' ||
    c === 'premium economy'
  ) {
    return 'PREMIUM_ECONOMY';
  }
  if (c === 'business') {
    return 'BUSINESS';
  }
  if (c === 'first') {
    return 'FIRST';
  }
  return as_string(kyte_cabin).toUpperCase();
};

const map_fare_type = (kyte_fare_type) => {
  const t = as_string(kyte_fare_type).toLowerCase();
  if (t === 'negociated' || t === 'negotiated') {
    return 'NEGOTIATED';
  }
  return as_string(kyte_fare_type).toUpperCase();
};

// ─── Shape builders ──────────────────────────────────────────────────────

const build_iso_datetime = (raw) => {
  const safe = raw || {};
  if (!safe.date) {
    return '';
  }
  // Ryanair ships HH:MM:SS, others HH:MM — only pad when needed (a blind
  // ":00" suffix turned 06:00:00 into 06:00:00:00 → Invalid Date). Empty
  // string is also a hazard: as_string returns `''` for `''` so we can't
  // rely on its fallback here.
  const raw_time = as_string(safe.time);
  const time = raw_time || '00:00';
  const iso_time = time.split(':').length >= 3 ? time : `${time}:00`;
  return `${safe.date}T${iso_time}`;
};

/**
 * Resolve airport / city names by preferring the provider's merged
 * dictionary (richer on confirm/retrieve via orderData.dictionaries),
 * falling back to the raw segment stub on the first search response.
 *
 * @param {object} provider
 * @param {object} kyte_endpoint segment endpoint stub
 * @returns {object} canonical Endpoint
 */
const build_endpoint = (provider, kyte_endpoint) => {
  const safe = kyte_endpoint || {};
  const airport = safe.airport || {};
  const city = airport.city || {};
  const iata = as_string(airport.code);
  return {
    iataCode: iata,
    terminal: as_string(safe.terminal),
    at: build_iso_datetime(safe),
    airportName: provider.display_airport(iata) || as_string(airport.name),
    cityCode: as_string(city.code),
    cityName: provider.display_location(iata) || as_string(city.name),
  };
};

const build_carrier = (provider, kyte_carrier) => {
  const safe = kyte_carrier || {};
  const code = as_string(safe.code);
  return {
    code,
    name: provider.display_carrier(code) || as_string(safe.name),
    alliance: as_string(safe.alliance),
  };
};

const build_segment = (provider, kyte_segment) => {
  const safe = kyte_segment || {};
  const operating = safe.operatingCarrier || safe.marketingCarrier || {};
  const aircraft = safe.aircraft || {};
  const flight_number = as_string(safe.flightNumber);
  const trimmed = flight_number.replace(/^0+/, '');
  return {
    id: as_string(safe.id),
    departure: build_endpoint(provider, safe.departure),
    arrival: build_endpoint(provider, safe.arrival),
    carrier: build_carrier(provider, safe.marketingCarrier),
    operatingCarrier: build_carrier(provider, operating),
    flightNumber: trimmed || flight_number,
    aircraft: {
      code: as_string(aircraft.code),
      name: as_string(aircraft.name),
    },
    duration: minutes_to_iso_duration(as_number(safe.duration)),
    numberOfStops: 0,
    blacklistedInEU: false,
    cabin: '',
    fareClass: '',
    fareBasis: '',
  };
};

const find_fare_for = (fares, segment_id, passenger_id) => {
  const keys = Object.keys(fares || {});
  for (let i = 0; i < keys.length; i += 1) {
    const fare = fares[keys[i]];
    if (
      !fare ||
      !Array.isArray(fare.segments) ||
      !Array.isArray(fare.passengers)
    ) {
      continue;
    }
    const covers_seg = fare.segments.indexOf(segment_id) !== -1;
    const covers_pax = fare.passengers.indexOf(passenger_id) !== -1;
    if (covers_seg && covers_pax) {
      return fare;
    }
  }
  return null;
};

const enrich_segments_with_fare = (segments, fares, first_passenger_id) =>
  segments.map((seg) => {
    const fare = find_fare_for(fares, seg.id, first_passenger_id);
    if (!fare) {
      return seg;
    }
    return {
      ...seg,
      cabin: map_cabin(fare.cabinType),
      fareClass: as_string(fare.fareClass),
      fareBasis: as_string(fare.fareBasisCode),
    };
  });

const build_itinerary = (provider, kyte_solution, index) => {
  const safe = kyte_solution || {};
  const segments = (Array.isArray(safe.segments) ? safe.segments : []).map(
    (seg) => build_segment(provider, seg)
  );
  return finalize_itinerary({
    id: as_string(safe.id, `itin-${index}`),
    duration: minutes_to_iso_duration(as_number(safe.totalDuration)),
    segments,
    empty_endpoint: build_empty_endpoint(),
  });
};

/**
 * Sum the bare-fare amount from a Kyte priceBreakdown[].
 *
 * Kyte ships the base row as either `{code:'BASE'}` (offer/details path)
 * or `{code:'FARE_PRICE', type:'base'}` (bundles + booking path), so we
 * match on either side.
 *
 * @param {Array} price_breakdown
 * @returns {number} amount in minor units, 0 when no base row is found
 */
const extract_base_amount = (price_breakdown) => {
  if (!Array.isArray(price_breakdown)) {
    return 0;
  }
  const base_line = price_breakdown.find((b) => {
    if (!b) return false;
    const code = as_string(b.code).toLowerCase();
    const type = as_string(b.type).toLowerCase();
    return code === 'base' || type === 'base';
  });
  return base_line ? as_number(base_line.amount) : 0;
};

const extract_taxes = (price_breakdown, currency) => {
  if (!Array.isArray(price_breakdown)) {
    return [];
  }
  return price_breakdown
    .filter((b) => b && b.type === 'tax')
    .map((b) => ({
      code: as_string(b.code),
      amount: minor_to_decimal(as_number(b.amount), currency.decimals),
    }));
};

/**
 * Parse a Kyte weight string ("10kg" / "23 kg") into `{weight, weightUnit}`.
 * Returns `{}` (not null) on empty/unparseable input so callers can spread
 * the result without stomping on adjacent fields.
 *
 * @param {Array<string>} weight_array
 * @returns {{weight?: number, weightUnit?: string}}
 */
const parse_baggage_weight = (weight_array) => {
  if (!Array.isArray(weight_array) || weight_array.length === 0) return {};
  const match = String(weight_array[0]).match(/^(\d+)\s*([a-zA-Z]+)$/);
  if (!match) return {};
  return { weight: as_number(match[1]), weightUnit: match[2].toLowerCase() };
};

/**
 * Find the baggageAllowance entry for a (pax, segment) cell on the
 * fare-details grid. Kyte allowance entries carry passengers[] +
 * segments[] which scope them to specific cells.
 *
 * @param {Array<object>} allowance_list
 * @param {string} type allowance type (handBag / cabin / checked / sportsEquipment)
 * @param {string|number} pax_id
 * @param {string} segment_id
 * @returns {object|null}
 */
const find_allowance = (allowance_list, type, pax_id, segment_id) => {
  if (!Array.isArray(allowance_list)) return null;
  return (
    allowance_list.find((entry) => {
      if (!entry || entry.type !== type) return false;
      const passengers = entry.passengers || [];
      const segments = entry.segments || [];
      return (
        passengers.includes(String(pax_id)) && segments.includes(segment_id)
      );
    }) || null
  );
};

/**
 * Project a Kyte baggageAllowance entry onto canonical IncludedBags.
 * Defaults to `{quantity: 0}` so callers always get a complete shape.
 *
 * @param {object|null} entry
 * @returns {{quantity: number, weight?: number, weightUnit?: string}}
 */
const allowance_to_included_bags = (entry) => {
  if (!entry) return { quantity: 0 };
  return {
    quantity: as_number(entry.quantity) || 0,
    ...parse_baggage_weight(entry.weight),
  };
};

// Build a FareDetailBySegment in Amadeus-native nested shape (canonical
// for legacy bag/markup readers). baggageAllowance / brandedFare /
// amenities arrive via offer-details — until then we under-claim
// (zero) so the resume sidebar never lies for Basic-style fares.
const build_fare_detail_for = (
  segment,
  fare,
  baggage_allowance,
  pax_id,
  pax_type
) => {
  const cabin = segment.cabin || (fare ? map_cabin(fare.cabinType) : '');
  const { code: carrier_code = '' } = segment.carrier || {};
  const handbag_entry = find_allowance(
    baggage_allowance,
    'handBag',
    pax_id,
    segment.id
  );
  // Sitting (non-infant) travelers are entitled to at least one handbag
  // even when the carrier doesn't list one in baggageAllowance — covers
  // missing field, empty list, or list with no handBag entry.
  const is_infant = as_string(pax_type).toLowerCase() === 'infant';
  const included_hand_bags = handbag_entry
    ? allowance_to_included_bags(handbag_entry)
    : is_infant
      ? { quantity: 0 }
      : { quantity: 1 };
  return {
    segmentId: segment.id,
    cabin,
    fareBasis: fare ? as_string(fare.fareBasisCode) : segment.fareBasis,
    fareClass: fare ? as_string(fare.fareClass) : segment.fareClass,
    brandedFare: fare ? as_string(fare.name) : '',
    brandedFareLabel: fare ? as_string(fare.name) : '',
    includedCheckedBags: allowance_to_included_bags(
      find_allowance(baggage_allowance, 'checked', pax_id, segment.id)
    ),
    includedCabinBags: allowance_to_included_bags(
      find_allowance(baggage_allowance, 'cabin', pax_id, segment.id)
    ),
    // handBag is Kyte-specific (no Amadeus equivalent — Amadeus uses a
    // heuristic). When the carrier offers it the entry carries the
    // truthful per-pax-segment count; when silent, non-infants default
    // to 1 (personal item is always allowed).
    includedHandBags: included_hand_bags,
    additionalServices: [],
    amenities: kyte_amenities_for_fare(fare, carrier_code),
  };
};

const build_traveler_pricing = (
  pax,
  all_segments,
  fares,
  currency,
  baggage_allowance
) => {
  const fare_details = all_segments.map((seg) => {
    const fare = find_fare_for(fares, seg.id, pax.id);
    return build_fare_detail_for(
      seg,
      fare,
      baggage_allowance,
      pax.id,
      pax.type
    );
  });
  const base_amount = extract_base_amount(pax.priceBreakdown);
  return {
    travelerId: as_string(pax.id),
    travelerType: map_pax_type(pax.type),
    associatedAdultId: as_string(pax.associatedAdultId),
    fareOption: 'STANDARD',
    price: with_currency_symbol({
      currency: as_string(currency.code),
      total: minor_to_decimal(as_number(pax.price), currency.decimals),
      base: minor_to_decimal(base_amount, currency.decimals),
      taxes: extract_taxes(pax.priceBreakdown, currency),
    }),
    fareDetailsBySegment: fare_details,
  };
};

const collect_fare_types = (fares) => {
  const seen = {};
  Object.keys(fares || {}).forEach((k) => {
    const fare = fares[k];
    if (!fare || !Array.isArray(fare.fareType)) {
      return;
    }
    fare.fareType.forEach((t) => {
      const mapped = map_fare_type(t);
      if (mapped) {
        seen[mapped] = true;
      }
    });
  });
  return Object.keys(seen);
};

const build_refund_and_change = (servicing_fees, currency) => {
  const fees = Array.isArray(servicing_fees) ? servicing_fees : [];
  const cancel = fees.find((f) => f && f.type === 'cancel');
  const change = fees.find((f) => f && f.type === 'change');
  return {
    refund: {
      allowed: !!cancel,
      penaltyCurrency: as_string(currency.code),
      penaltyAmount: cancel
        ? minor_to_decimal(as_number(cancel.amount), currency.decimals)
        : '0',
      notes:
        cancel && Array.isArray(cancel.remarks) ? cancel.remarks.join(' ') : '',
    },
    change: {
      allowed: !!change,
      penaltyCurrency: as_string(currency.code),
      penaltyAmount: change
        ? minor_to_decimal(as_number(change.amount), currency.decimals)
        : '0',
      notes:
        change && Array.isArray(change.remarks) ? change.remarks.join(' ') : '',
    },
  };
};

const infer_fare_info = (
  itineraries,
  fares,
  refund_allowed,
  change_allowed
) => {
  const first_segment = itineraries[0] && itineraries[0].segments[0];
  const cabin = first_segment ? first_segment.cabin : '';
  const fare_ids = Object.keys(fares || {});
  const first_fare = fare_ids.length > 0 ? fares[fare_ids[0]] : null;
  return {
    brand: first_fare ? as_string(first_fare.name) : '',
    cabin,
    refundable: refund_allowed,
    changeable: change_allowed,
    rulesUri: '',
  };
};

const collect_amenities = (traveler_pricings) =>
  lift_first_fare_amenities(traveler_pricings);

// ─── Response shaping ────────────────────────────────────────────────────

/**
 * Flatten a raw shop-flights response into per-offer payloads ready for
 * validate() / normalize(). Resolves flightSolutions ID references into
 * the actual solution objects.
 *
 * @param {object} provider
 * @param {object} response shop-flights response
 * @returns {Array<object>} per-offer raw payloads
 */
export const split_offers_from_response = (provider, response) => {
  if (!response || typeof response !== 'object') {
    return [];
  }
  const offers = response.offers || {};
  const solutions = response.flightSolutions || {};
  return Object.keys(offers).map((offer_id) => {
    const offer = offers[offer_id];
    const solution_ids = Array.isArray(offer.flightSolutions)
      ? offer.flightSolutions
      : [];
    const resolved = solution_ids
      .map((fs_id) => solutions[fs_id])
      .filter((s) => !!s);
    return {
      ...offer,
      _provider: provider.providerId,
      flightSolutions: resolved,
    };
  });
};

/**
 * Fold an offer-details response entry into the raw offer so normalize()
 * sees the enriched fields (baggageAllowance, servicingFees, …) on its
 * next pass. The full entry is kept on `_details` for fields not
 * explicitly lifted (disclosures, fares, …).
 *
 * @param {object} raw search-time raw offer
 * @param {object|null} details_entry kyte.details body
 * @returns {object} merged raw offer
 */
export const merge_details_into_raw = (raw, details_entry) => {
  if (!details_entry) {
    return raw;
  }
  return {
    ...raw,
    servicingFees: details_entry.servicingFees || raw.servicingFees,
    baggageAllowance: details_entry.baggageAllowance || raw.baggageAllowance,
    _details: details_entry,
  };
};

/**
 * Build a canonical FlightOffer directly from a Kyte RetrieveBooking
 * response so the FE renders the actually-booked fare (cancellara's
 * recovery path can return a Basic booking against a FE offer pinned
 * to Regular — without this the brand would stay stale).
 *
 * Reuses the search-time builders (build_itinerary, build_fare_detail_for,
 * collect_amenities, …) against booking-sourced inputs — no synthetic
 * raw, no double-pass.
 *
 * @param {object} provider
 * @param {object} booking RetrieveBooking response
 * @param {object} [options]
 * @param {string} [options.existingOfferId] FE-held offerId; falls back to bookingId
 * @param {Array<object>} [options.baggageAllowance] from a chained kyte.details fetch
 * @returns {object} canonical FlightOffer
 */
export const normalize_booking = (provider, booking, options = {}) => {
  const { existingOfferId = '', baggageAllowance = [] } = options;
  const safe = booking || {};
  const itinerary = safe.itinerary || {};
  const flight_solutions =
    itinerary.flightSolutions && typeof itinerary.flightSolutions === 'object'
      ? Object.values(itinerary.flightSolutions)
      : [];
  const fares = safe.fares || {};
  const passengers = Array.isArray(safe.passengers) ? safe.passengers : [];
  const currency = safe.currency || { code: 'EUR', decimals: 2 };

  if (
    !flight_solutions.length ||
    !passengers.length ||
    !Object.keys(fares).length
  ) {
    throw new Error(
      '[KyteProvider] booking does not conform (missing itinerary / passengers / fares)'
    );
  }

  // Itineraries: build segments + enrich with the fare each segment
  // belongs to (cabin / fareClass / fareBasis come from there).
  const raw_itineraries = flight_solutions.map((fs, idx) =>
    build_itinerary(provider, fs, idx)
  );
  const first_pax_id = passengers[0].id;
  const itineraries = raw_itineraries.map((itin) => {
    const enriched = enrich_segments_with_fare(
      itin.segments,
      fares,
      first_pax_id
    );
    return {
      ...itin,
      segments: enriched,
      firstDeparture: enriched[0] ? enriched[0].departure : itin.firstDeparture,
      lastArrival: enriched[enriched.length - 1]
        ? enriched[enriched.length - 1].arrival
        : itin.lastArrival,
    };
  });
  const all_segments = itineraries.reduce(
    (acc, itin) => acc.concat(itin.segments),
    []
  );

  // pax.price.total = baseFare + bundle premium so Σ(per-pax total)
  // matches offer.price.total (= totalCost − shopped). Shopped
  // ancillaries live on price.additionalServices via
  // fold_committed_ancillaries so the resume sidebar lists them once.
  const breakdown = Array.isArray(safe.pricingBreakdown)
    ? safe.pricingBreakdown
    : [];
  const pax_fare = {};
  const pax_total_fare = {};
  breakdown.forEach((row) => {
    const { passengerId, price = 0, category, type, id } = row || {};
    if (!passengerId) return;
    if (category === 'fare' && type === 'baseFare') {
      pax_fare[passengerId] = (pax_fare[passengerId] || 0) + as_number(price);
      pax_total_fare[passengerId] =
        (pax_total_fare[passengerId] || 0) + as_number(price);
      return;
    }
    if (category === 'ancillary' && is_bundle_service(type, id)) {
      pax_total_fare[passengerId] =
        (pax_total_fare[passengerId] || 0) + as_number(price);
    }
  });

  // baggageAllowance is empty on retrieve (only /details ships it) so
  // includedHandBags / includedCabinBags / includedCheckedBags come
  // back at 0. Per-pax taxes aren't separated either.
  const traveler_pricings_raw = passengers.map((pax) => {
    const fare_details = all_segments.map((seg) => {
      const fare = find_fare_for(fares, seg.id, pax.id);
      return build_fare_detail_for(
        seg,
        fare,
        baggageAllowance,
        pax.id,
        pax.type
      );
    });
    return {
      travelerId: as_string(pax.id),
      travelerType: map_pax_type(pax.type),
      associatedAdultId: as_string(pax.associatedAdultId),
      fareOption: 'STANDARD',
      price: with_currency_symbol({
        currency: as_string(currency.code),
        total: minor_to_decimal(pax_total_fare[pax.id] || 0, currency.decimals),
        base: minor_to_decimal(pax_fare[pax.id] || 0, currency.decimals),
        taxes: [],
      }),
      fareDetailsBySegment: fare_details,
    };
  });
  const traveler_pricings = ensure_infant_associations(traveler_pricings_raw);

  // Seed total/grandTotal at booking.totalCost — fold_committed_ancillaries
  // downstream refines `total = totalCost − shopped`.
  const total_base = passengers.reduce(
    (sum, pax) => sum + (pax_fare[pax.id] || 0),
    0
  );
  const total_decimal = minor_to_decimal(
    as_number(safe.totalCost),
    currency.decimals
  );
  const price = with_currency_symbol({
    currency: as_string(currency.code),
    base: minor_to_decimal(total_base, currency.decimals),
    _markup_base: minor_to_decimal(total_base, currency.decimals),
    total: total_decimal,
    grandTotal: total_decimal,
    fees: [],
    additionalServices: [],
  });

  // Retrieve doesn't carry servicingFees — assume non-refundable /
  // non-changeable until /details enrichment lands.
  const refund = {
    allowed: false,
    penaltyCurrency: as_string(currency.code),
    penaltyAmount: '0',
    notes: '',
  };
  const change = {
    allowed: false,
    penaltyCurrency: as_string(currency.code),
    penaltyAmount: '0',
    notes: '',
  };
  const fare = infer_fare_info(
    itineraries,
    fares,
    refund.allowed,
    change.allowed
  );
  const amenities = collect_amenities(traveler_pricings);

  const first_itin = itineraries[0];
  const last_itin = itineraries[itineraries.length - 1];
  const ticketing_hint = as_string(safe.ticketTimeLimit || '');
  const { code: owner = '' } = safe.ticketingCarrier || {};
  // FE-held offerId wins so /details can call against it; bookingId
  // is the cold-start fallback.
  const offer_id = as_string(existingOfferId) || as_string(safe.id);
  const leg_count =
    itinerary.legs && typeof itinerary.legs === 'object'
      ? Object.keys(itinerary.legs).length
      : flight_solutions.length;

  return {
    _provider: provider.providerId,
    _raw: build_frozen_raw(safe),
    _meta: build_meta(provider.version),
    _key: `${provider.providerId}:${offer_id}`,

    id: offer_id,
    providerOfferId: offer_id,
    providerSessionId: '',
    source: 'KYTE',

    firstDeparture: first_itin
      ? first_itin.firstDeparture
      : build_empty_endpoint(),
    firstArrival: first_itin ? first_itin.lastArrival : build_empty_endpoint(),
    lastDeparture: last_itin
      ? last_itin.firstDeparture
      : build_empty_endpoint(),
    lastArrival: last_itin ? last_itin.lastArrival : build_empty_endpoint(),
    totalDuration: sum_itinerary_durations(itineraries),
    oneWay: leg_count === 1,
    numberOfBookableSeats: 9,
    lastTicketingDate: ticketing_hint.slice(0, 10),

    validatingAirlineCodes: owner ? [owner] : [],
    marketingCarrierCodes: collect_marketing_carriers(itineraries),

    itineraries,
    travelerPricings: traveler_pricings,

    price,
    fare,
    refund,
    change,
    payment: {
      acceptedMethods: [],
      requires3DS: provider.has_capability('supports3DSAlwaysOn'),
      paymentContext: '',
    },
    amenities,

    _fareTypes: collect_fare_types(fares),
  };
};

/**
 * Map a FlightOffer's travelerPricings to the `passengers[]` array
 * shape required by the offer-details request body.
 *
 * @param {object} flight_offer
 * @returns {Array<{id: string}>}
 */
export const build_passengers_for_details = (flight_offer) => {
  const pricings = flight_offer.travelerPricings || [];
  return pricings.map((tp) => ({ id: tp.travelerId }));
};

/**
 * Inverse of build_fare_detail_for: project a canonical FlightOffer's
 * includedCheckedBags / includedCabinBags / includedHandBags onto a
 * Kyte-shaped baggageAllowance[] (the shape build_fare_detail_for reads
 * back via find_allowance). One entry per (pax × segment × type) when
 * the quantity is positive — Kyte's response shape is sparse, so a
 * missing entry IS the "no allowance" signal and zero-emission keeps
 * the round-trip lossless without leaking phantom rows.
 *
 * @param {object} flight_offer canonical FlightOffer
 * @returns {Array<{type: string, quantity: number, passengers: Array<string>, segments: Array<string>, weight?: Array<string>}>}
 */
export const build_baggage_allowance_from_offer = (flight_offer) => {
  const { travelerPricings = [] } = flight_offer || {};
  const cells = [
    { key: 'includedCheckedBags', type: 'checked' },
    { key: 'includedCabinBags', type: 'cabin' },
    { key: 'includedHandBags', type: 'handBag' },
  ];
  const out = [];
  travelerPricings.forEach((tp) => {
    const { travelerId = '', fareDetailsBySegment = [] } = tp || {};
    const traveler_id = as_string(travelerId);
    if (!traveler_id) return;
    fareDetailsBySegment.forEach((fd) => {
      const safe_fd = fd || {};
      const segment_id = as_string(safe_fd.segmentId);
      if (!segment_id) return;
      cells.forEach(({ key, type }) => {
        const {
          quantity = 0,
          weight = 0,
          weightUnit = '',
        } = safe_fd[key] || {};
        const n = as_number(quantity);
        if (n <= 0) return;
        const entry = {
          type,
          quantity: n,
          passengers: [traveler_id],
          segments: [segment_id],
        };
        const w = as_number(weight);
        const u = as_string(weightUnit);
        if (w > 0 && u) entry.weight = [`${w}${u}`];
        out.push(entry);
      });
    });
  });
  return out;
};

// ─── Contract ────────────────────────────────────────────────────────────

/**
 * Pre-normalize sanity check. Confirms a per-offer raw payload has all
 * fields normalize() needs (id, resolved flightSolutions, currency,
 * totalPrice, passengers, fares).
 *
 * @param {object} _provider unused (signature parity with base contract)
 * @param {object} raw per-offer raw payload
 * @returns {boolean}
 */
export const validate = (_provider, raw) => {
  if (!raw || typeof raw !== 'object') {
    return false;
  }
  if (typeof raw.id !== 'string' || raw.id.length === 0) {
    return false;
  }
  if (!is_non_empty_array(raw.flightSolutions)) {
    return false;
  }
  const all_resolved = raw.flightSolutions.every(
    (fs) => fs && is_non_empty_array(fs.segments)
  );
  if (!all_resolved) {
    return false;
  }
  if (!raw.currency || typeof raw.currency.code !== 'string') {
    return false;
  }
  if (typeof raw.totalPrice !== 'number') {
    return false;
  }
  if (!is_non_empty_array(raw.passengers)) {
    return false;
  }
  if (!is_non_empty_object(raw.fares)) {
    return false;
  }
  return true;
};

/**
 * Project a per-offer raw payload onto the canonical FlightOffer shape.
 * Throws if the raw fails validate() — caller should run
 * split_offers_from_response first.
 *
 * @param {object} provider
 * @param {object} raw per-offer payload
 * @returns {object} canonical FlightOffer
 */
export const normalize = (provider, raw) => {
  if (!validate(provider, raw)) {
    throw new Error(
      '[KyteProvider] payload does not conform (did you run split_offers_from_response?)'
    );
  }

  const {
    id,
    flightSolutions,
    owner,
    currency,
    totalPrice,
    passengers,
    fares,
    servicingFees,
    baggageAllowance: baggage_allowance = [],
    priceGuaranteeExpiration,
    expiration,
    singleJourneyLeg,
  } = raw;

  // Itineraries + enrich with fare cross-reference.
  const raw_itineraries = flightSolutions.map((fs, idx) =>
    build_itinerary(provider, fs, idx)
  );
  const first_pax_id = passengers[0].id;
  const itineraries = raw_itineraries.map((itin) => {
    const enriched = enrich_segments_with_fare(
      itin.segments,
      fares,
      first_pax_id
    );
    return {
      ...itin,
      segments: enriched,
      firstDeparture: enriched[0] ? enriched[0].departure : itin.firstDeparture,
      lastArrival: enriched[enriched.length - 1]
        ? enriched[enriched.length - 1].arrival
        : itin.lastArrival,
    };
  });
  const all_segments = itineraries.reduce(
    (acc, itin) => acc.concat(itin.segments),
    []
  );

  // Kyte omits associatedAdultId on infants, so we synthesize the link
  // here — downstream (seat picker, BookFlight, …) trusts travelerPricings
  // as the single source of truth.
  const raw_traveler_pricings = passengers.map((pax) =>
    build_traveler_pricing(
      pax,
      all_segments,
      fares,
      currency,
      baggage_allowance
    )
  );
  const traveler_pricings = ensure_infant_associations(raw_traveler_pricings);
  const total_base = passengers.reduce(
    (sum, pax) => sum + extract_base_amount(pax.priceBreakdown),
    0
  );
  const total_decimal = minor_to_decimal(
    as_number(totalPrice),
    currency.decimals
  );
  const price = with_currency_symbol({
    currency: as_string(currency.code),
    base: minor_to_decimal(total_base, currency.decimals),
    _markup_base: minor_to_decimal(total_base, currency.decimals),
    total: total_decimal,
    grandTotal: total_decimal,
    fees: [],
    additionalServices: [],
  });

  // Refund / change info + inferred fare + amenities.
  const { refund, change } = build_refund_and_change(servicingFees, currency);
  const fare = infer_fare_info(
    itineraries,
    fares,
    refund.allowed,
    change.allowed
  );
  const amenities = collect_amenities(traveler_pricings);

  const first_itin = itineraries[0];
  const last_itin = itineraries[itineraries.length - 1];
  const ticketing_hint = as_string(priceGuaranteeExpiration || expiration);

  const offer_id = as_string(id);

  return {
    _provider: provider.providerId,
    _raw: build_frozen_raw(raw),
    _meta: build_meta(provider.version),
    _key: `${provider.providerId}:${offer_id}`,

    id: offer_id,
    providerOfferId: offer_id,
    providerSessionId: '',
    source: 'KYTE',

    firstDeparture: first_itin
      ? first_itin.firstDeparture
      : build_empty_endpoint(),
    firstArrival: first_itin ? first_itin.lastArrival : build_empty_endpoint(),
    lastDeparture: last_itin
      ? last_itin.firstDeparture
      : build_empty_endpoint(),
    lastArrival: last_itin ? last_itin.lastArrival : build_empty_endpoint(),
    totalDuration: sum_itinerary_durations(itineraries),
    oneWay: flightSolutions.length === 1 || singleJourneyLeg === true,
    numberOfBookableSeats: 9,
    lastTicketingDate: ticketing_hint.slice(0, 10),

    validatingAirlineCodes: owner ? [owner] : [],
    marketingCarrierCodes: collect_marketing_carriers(itineraries),

    itineraries,
    travelerPricings: traveler_pricings,

    price,
    fare,
    refund,
    change,
    payment: {
      acceptedMethods: [],
      requires3DS: provider.has_capability('supports3DSAlwaysOn'),
      paymentContext: '',
    },
    amenities,

    _fareTypes: collect_fare_types(fares),
  };
};
